import React, { useState, useEffect } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

interface DataCollectionPreviewProps {
  onClick?: () => void;
}

interface QuestionData {
  id: string;
  type: string;
  subject: string;
  question: string;
  answer: string;
  isCorrect: boolean;
  metrics: {
    responseTime: string;
    accuracy: number;
    attempts: number;
    confidence: string;
  };
  tags: string[];
}

const DataCollectionPreview: React.FC<DataCollectionPreviewProps> = ({ onClick }) => {
  const [activeMetricIndex, setActiveMetricIndex] = useState(0);
  const [pulseIndex, setPulseIndex] = useState(0);
  const [expandedQuestion, setExpandedQuestion] = useState<string | null>('q1');
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);

  // Sample questions showing variety and complexity
  const questions: QuestionData[] = [
    {
      id: 'q1',
      type: 'Algebra',
      subject: 'Year 8 Maths',
      question: 'Solve for x: 3(x + 4) = 2x + 15',
      answer: 'x = 3',
      isCorrect: true,
      metrics: {
        responseTime: '18.2s',
        accuracy: 92,
        attempts: 1,
        confidence: 'High',
      },
      tags: ['Linear Equations', 'Distributive Property', 'Problem Solving'],
    },
    {
      id: 'q2',
      type: 'Fractions',
      subject: 'Year 6 Maths',
      question: 'Calculate: ¾ ÷ ⅔ + ½',
      answer: '1⅝',
      isCorrect: true,
      metrics: {
        responseTime: '24.7s',
        accuracy: 78,
        attempts: 2,
        confidence: 'Medium',
      },
      tags: ['Division of Fractions', 'Mixed Operations', 'Order of Operations'],
    },
    {
      id: 'q3',
      type: 'Geometry',
      subject: 'Year 9 Maths',
      question: 'Find the area of a triangle with vertices at (0,0), (4,0), (2,5)',
      answer: '10 units²',
      isCorrect: false,
      metrics: {
        responseTime: '45.3s',
        accuracy: 65,
        attempts: 3,
        confidence: 'Low',
      },
      tags: ['Coordinate Geometry', 'Area Formula', 'Vertices'],
    },
  ];

  const currentQuestion = questions[activeQuestionIndex];

  // Cycle through questions for preview animation
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveQuestionIndex((prev) => (prev + 1) % questions.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [questions.length]);

  // Cycle through metrics for animation
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveMetricIndex((prev) => (prev + 1) % 4);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Pulse animation for data points
  useEffect(() => {
    const interval = setInterval(() => {
      setPulseIndex((prev) => (prev + 1) % 6);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const metrics = [
    { label: 'Response Time', value: currentQuestion.metrics.responseTime, color: '#0ea5e9', icon: '⏱' },
    { label: 'Accuracy', value: `${currentQuestion.metrics.accuracy}%`, color: '#10b981', icon: '✓' },
    { label: 'Attempts', value: currentQuestion.metrics.attempts.toString(), color: '#f59e0b', icon: '↻' },
    { label: 'Confidence', value: currentQuestion.metrics.confidence, color: '#8b5cf6', icon: '◆' },
  ];

  const dataPoints = [
    { x: 40, y: 30 },
    { x: 80, y: 50 },
    { x: 120, y: 35 },
    { x: 160, y: 60 },
    { x: 200, y: 45 },
    { x: 240, y: 55 },
  ];

  const toggleQuestion = (id: string) => {
    setExpandedQuestion(expandedQuestion === id ? null : id);
  };

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'High': return '#10b981';
      case 'Medium': return '#f59e0b';
      case 'Low': return '#f43f5e';
      default: return '#94a3b8';
    }
  };

  return (
    <div
      className="relative cursor-pointer transition-all duration-300 hover:scale-[1.02] group"
      onClick={onClick}
      style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
        width: '340px',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-2 border-b flex items-center justify-between"
        style={{
          borderColor: 'rgba(255, 255, 255, 0.1)',
          background: 'rgba(16, 185, 129, 0.05)',
        }}
      >
        <span className="text-xs font-semibold" style={{ color: '#94a3b8' }}>
          Real-Time Data Collection
        </span>
        <div className="flex items-center gap-1">
          <div
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ background: '#10b981' }}
          />
          <span className="text-[10px]" style={{ color: '#10b981' }}>
            LIVE
          </span>
        </div>
      </div>

      {/* Questions List - Collapsible */}
      <div className="max-h-[180px] overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
        {questions.map((q, index) => (
          <div
            key={q.id}
            className="transition-all duration-300"
            style={{
              borderBottom: '1px solid rgba(255,255,255,0.05)',
              background: activeQuestionIndex === index ? 'rgba(14, 165, 233, 0.05)' : 'transparent',
            }}
          >
            {/* Question Header - Always visible */}
            <div
              className="px-3 py-2 flex items-center justify-between cursor-pointer hover:bg-white/5"
              onClick={(e) => {
                e.stopPropagation();
                toggleQuestion(q.id);
              }}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div
                  className="w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                  style={{
                    background: q.isCorrect ? 'rgba(16, 185, 129, 0.2)' : 'rgba(244, 63, 94, 0.2)',
                    color: q.isCorrect ? '#10b981' : '#f43f5e',
                  }}
                >
                  {q.isCorrect ? '✓' : '✗'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded"
                      style={{ background: 'rgba(14, 165, 233, 0.2)', color: '#0ea5e9' }}
                    >
                      {q.type}
                    </span>
                    <span className="text-[9px]" style={{ color: '#64748b' }}>
                      {q.subject}
                    </span>
                  </div>
                  <p
                    className="text-[11px] truncate mt-0.5"
                    style={{ color: '#e2e8f0' }}
                  >
                    {q.question}
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0 ml-2">
                {expandedQuestion === q.id ? (
                  <ChevronUpIcon className="w-4 h-4" style={{ color: '#64748b' }} />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" style={{ color: '#64748b' }} />
                )}
              </div>
            </div>

            {/* Expanded Details */}
            {expandedQuestion === q.id && (
              <div
                className="px-3 pb-2 space-y-2"
                style={{ background: 'rgba(0, 0, 0, 0.1)' }}
              >
                {/* Answer */}
                <div className="flex items-center gap-2">
                  <span className="text-[10px]" style={{ color: '#64748b' }}>Answer:</span>
                  <span
                    className="text-[11px] font-mono px-2 py-0.5 rounded"
                    style={{
                      background: q.isCorrect ? 'rgba(16, 185, 129, 0.15)' : 'rgba(244, 63, 94, 0.15)',
                      color: q.isCorrect ? '#10b981' : '#f43f5e',
                    }}
                  >
                    {q.answer}
                  </span>
                </div>

                {/* Quick Metrics Row */}
                <div className="flex items-center gap-3 text-[9px]">
                  <span style={{ color: '#64748b' }}>
                    ⏱ {q.metrics.responseTime}
                  </span>
                  <span style={{ color: '#64748b' }}>
                    ↻ {q.metrics.attempts} {q.metrics.attempts === 1 ? 'attempt' : 'attempts'}
                  </span>
                  <span style={{ color: getConfidenceColor(q.metrics.confidence) }}>
                    ◆ {q.metrics.confidence}
                  </span>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-1">
                  {q.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-[8px] px-1.5 py-0.5 rounded"
                      style={{
                        background: 'rgba(139, 92, 246, 0.15)',
                        color: '#a78bfa',
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Aggregated Metrics Grid */}
      <div className="grid grid-cols-4 gap-1 p-2" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        {metrics.map((metric, index) => (
          <div
            key={metric.label}
            className="p-1.5 rounded transition-all duration-300 text-center"
            style={{
              background: activeMetricIndex === index
                ? `${metric.color}15`
                : 'rgba(255, 255, 255, 0.02)',
              border: `1px solid ${activeMetricIndex === index ? `${metric.color}40` : 'transparent'}`,
            }}
          >
            <div className="text-[9px]" style={{ color: '#64748b' }}>
              {metric.label}
            </div>
            <div
              className="text-xs font-bold"
              style={{ color: activeMetricIndex === index ? metric.color : '#94a3b8' }}
            >
              {metric.value}
            </div>
          </div>
        ))}
      </div>

      {/* Data Flow Visualization - Compact */}
      <div className="px-2 pb-2">
        <svg width="320" height="45" className="block">
          <defs>
            <linearGradient id="data-line-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.2" />
              <stop offset="50%" stopColor="#10b981" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.2" />
            </linearGradient>
            <filter id="data-glow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Connection line */}
          <path
            d={`M ${dataPoints.map(p => `${p.x + 20},${p.y - 5}`).join(' L ')}`}
            fill="none"
            stroke="url(#data-line-gradient)"
            strokeWidth="2"
            strokeLinecap="round"
          />

          {/* Data points with pulse animation */}
          {dataPoints.map((point, index) => (
            <g key={index}>
              <circle
                cx={point.x + 20}
                cy={point.y - 5}
                r={pulseIndex === index ? 8 : 4}
                fill="none"
                stroke="#10b981"
                strokeWidth="1"
                opacity={pulseIndex === index ? 0.5 : 0}
                style={{ transition: 'all 0.3s ease' }}
              />
              <circle
                cx={point.x + 20}
                cy={point.y - 5}
                r="3"
                fill={pulseIndex === index ? '#10b981' : '#0ea5e9'}
                filter="url(#data-glow)"
                style={{ transition: 'fill 0.3s ease' }}
              />
            </g>
          ))}

          {/* Arrow at end */}
          <polygon
            points="290,45 300,50 290,55"
            fill="#8b5cf6"
            opacity="0.8"
            transform="translate(0, -15)"
          />
        </svg>
      </div>

      {/* Footer */}
      <div
        className="px-3 py-1.5 flex items-center justify-between"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <div className="flex items-center gap-2">
          <div className="flex -space-x-1">
            {['#0ea5e9', '#10b981', '#8b5cf6'].map((color, i) => (
              <div
                key={i}
                className="w-2.5 h-2.5 rounded-full border"
                style={{ background: color, borderColor: '#1e293b' }}
              />
            ))}
          </div>
          <span className="text-[9px]" style={{ color: '#64748b' }}>
            Updating knowledge graph
          </span>
        </div>
        <span className="text-[9px]" style={{ color: '#10b981' }}>
          1,247 data points today
        </span>
      </div>

      {/* Expand overlay */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20 pointer-events-none">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.9) 0%, rgba(6, 182, 212, 0.9) 100%)',
            boxShadow: '0 8px 32px rgba(16, 185, 129, 0.4)',
          }}
        >
          <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default DataCollectionPreview;
